import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';
import LeaveTypeService from '../services/leaveTypeService';
import LeaveBalanceService from '../services/leaveBalanceService';
import WeeklyOffService from '../services/weeklyOffService';

const LeavePolicyManagementPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('leave-types');
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [weeklyOffs, setWeeklyOffs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Leave Type Form State
  const [leaveTypeForm, setLeaveTypeForm] = useState({
    name: '',
    description: '',
    maxLeavesPerYear: '',
    isPaid: true,
    color: '#3B82F6'
  });
  const [editingLeaveType, setEditingLeaveType] = useState(null);

  // Weekly Off State
  const [selectedWeeklyOffs, setSelectedWeeklyOffs] = useState([]);

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    if (activeTab === 'leave-types') {
      fetchLeaveTypes();
    } else if (activeTab === 'leave-balances') {
      fetchLeaveBalances();
    } else if (activeTab === 'weekly-offs') {
      fetchWeeklyOffs();
    }
  }, [activeTab]);

  const fetchLeaveTypes = async () => {
    try {
      setLoading(true);
      const response = await LeaveTypeService.getAllLeaveTypes();
      setLeaveTypes(response.data);
    } catch (error) {
      setError('Failed to fetch leave types');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveBalances = async () => {
    try {
      setLoading(true);
      const response = await LeaveBalanceService.getAllLeaveBalances();
      setLeaveBalances(response.data);
    } catch (error) {
      setError('Failed to fetch leave balances');
    } finally {
      setLoading(false);
    }
  };

  const fetchWeeklyOffs = async () => {
    try {
      setLoading(true);
      const response = await WeeklyOffService.getAllWeeklyOffs();
      setWeeklyOffs(response.data);
      setSelectedWeeklyOffs(response.data.map(wo => wo.dayOfWeek));
    } catch (error) {
      setError('Failed to fetch weekly offs');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      if (editingLeaveType) {
        await LeaveTypeService.updateLeaveType(editingLeaveType._id, leaveTypeForm);
      } else {
        await LeaveTypeService.createLeaveType(leaveTypeForm);
      }

      setLeaveTypeForm({
        name: '',
        description: '',
        maxLeavesPerYear: '',
        isPaid: true,
        color: '#3B82F6'
      });
      setEditingLeaveType(null);
      fetchLeaveTypes();
    } catch (error) {
      setError(error.error || 'Failed to save leave type');
    } finally {
      setLoading(false);
    }
  };

  const handleEditLeaveType = (leaveType) => {
    setLeaveTypeForm({
      name: leaveType.name,
      description: leaveType.description || '',
      maxLeavesPerYear: leaveType.maxLeavesPerYear,
      isPaid: leaveType.isPaid,
      color: leaveType.color
    });
    setEditingLeaveType(leaveType);
  };

  const handleDeleteLeaveType = async (leaveTypeId) => {
    if (!window.confirm('Are you sure you want to delete this leave type?')) {
      return;
    }

    try {
      await LeaveTypeService.deleteLeaveType(leaveTypeId);
      fetchLeaveTypes();
    } catch (error) {
      setError(error.error || 'Failed to delete leave type');
    }
  };

  const handleSeedDefaultTypes = async () => {
    try {
      setLoading(true);
      await LeaveTypeService.seedDefaultLeaveTypes();
      fetchLeaveTypes();
    } catch (error) {
      setError(error.error || 'Failed to seed default leave types');
    } finally {
      setLoading(false);
    }
  };

  const handleWeeklyOffChange = (dayOfWeek) => {
    setSelectedWeeklyOffs(prev => {
      if (prev.includes(dayOfWeek)) {
        return prev.filter(day => day !== dayOfWeek);
      } else {
        return [...prev, dayOfWeek];
      }
    });
  };

  const handleSaveWeeklyOffs = async () => {
    try {
      setLoading(true);
      await WeeklyOffService.setWeeklyOffConfiguration(selectedWeeklyOffs);
      fetchWeeklyOffs();
    } catch (error) {
      setError(error.error || 'Failed to save weekly off configuration');
    } finally {
      setLoading(false);
    }
  };

  if (!['HRO', 'ADMIN', 'CHAIRMAN'].includes(user?.role)) {
    return (
      <MainLayout>
        <div className="page-container">
          <div className="error-message">
            Access denied. Only HRO, Admin, and Chairman can access this page.
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page-container">
        <div className="page-header">
          <h1>Leave Policy Management</h1>
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError('')} className="btn btn-sm btn-secondary">
              Dismiss
            </button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'leave-types' ? 'active' : ''}`}
            onClick={() => setActiveTab('leave-types')}
          >
            Leave Types
          </button>
          <button
            className={`tab-button ${activeTab === 'leave-balances' ? 'active' : ''}`}
            onClick={() => setActiveTab('leave-balances')}
          >
            Leave Balances
          </button>
          <button
            className={`tab-button ${activeTab === 'weekly-offs' ? 'active' : ''}`}
            onClick={() => setActiveTab('weekly-offs')}
          >
            Weekly Offs
          </button>
        </div>

        {/* Leave Types Tab */}
        {activeTab === 'leave-types' && (
          <div className="tab-content">
            <div className="section-header">
              <h2>Leave Types Management</h2>
              <button
                className="btn btn-secondary"
                onClick={handleSeedDefaultTypes}
                disabled={loading}
              >
                Seed Default Types
              </button>
            </div>

            {/* Leave Type Form */}
            <div className="form-section">
              <h3>{editingLeaveType ? 'Edit Leave Type' : 'Create New Leave Type'}</h3>
              <form onSubmit={handleLeaveTypeSubmit} className="leave-type-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      value={leaveTypeForm.name}
                      onChange={(e) => setLeaveTypeForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Sick Leave"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="maxLeavesPerYear">Max Leaves Per Year *</label>
                    <input
                      type="number"
                      id="maxLeavesPerYear"
                      value={leaveTypeForm.maxLeavesPerYear}
                      onChange={(e) => setLeaveTypeForm(prev => ({ ...prev, maxLeavesPerYear: parseInt(e.target.value) }))}
                      min="0"
                      max="365"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="color">Color</label>
                    <input
                      type="color"
                      id="color"
                      value={leaveTypeForm.color}
                      onChange={(e) => setLeaveTypeForm(prev => ({ ...prev, color: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={leaveTypeForm.isPaid}
                        onChange={(e) => setLeaveTypeForm(prev => ({ ...prev, isPaid: e.target.checked }))}
                      />
                      Paid Leave
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={leaveTypeForm.description}
                    onChange={(e) => setLeaveTypeForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Optional description..."
                    rows="3"
                  />
                </div>

                <div className="form-actions">
                  {editingLeaveType && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setEditingLeaveType(null);
                        setLeaveTypeForm({
                          name: '',
                          description: '',
                          maxLeavesPerYear: '',
                          isPaid: true,
                          color: '#3B82F6'
                        });
                      }}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : editingLeaveType ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>

            {/* Leave Types List */}
            <div className="table-container">
              {loading ? (
                <div className="loading-message">Loading leave types...</div>
              ) : leaveTypes.length === 0 ? (
                <div className="empty-state">
                  <p>No leave types found.</p>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Max/Year</th>
                      <th>Type</th>
                      <th>Color</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveTypes.map((type) => (
                      <tr key={type._id}>
                        <td>{type.name}</td>
                        <td>{type.maxLeavesPerYear} days</td>
                        <td>
                          <span className={`badge ${type.isPaid ? 'badge-success' : 'badge-warning'}`}>
                            {type.isPaid ? 'Paid' : 'Unpaid'}
                          </span>
                        </td>
                        <td>
                          <div
                            className="color-preview"
                            style={{ backgroundColor: type.color }}
                            title={type.color}
                          ></div>
                        </td>
                        <td>{type.description || 'No description'}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleEditLeaveType(type)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteLeaveType(type._id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Leave Balances Tab */}
        {activeTab === 'leave-balances' && (
          <div className="tab-content">
            <div className="section-header">
              <h2>Leave Balances Overview</h2>
              <p>View and manage employee leave balances</p>
            </div>

            <div className="table-container">
              {loading ? (
                <div className="loading-message">Loading leave balances...</div>
              ) : leaveBalances.length === 0 ? (
                <div className="empty-state">
                  <p>No leave balances found.</p>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Leave Type</th>
                      <th>Year</th>
                      <th>Total</th>
                      <th>Used</th>
                      <th>Remaining</th>
                      <th>Assigned By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveBalances.map((balance) => {
                      const employeeName = balance.userId?.name ||
                        balance.userId?.fullName ||
                        `${balance.userId?.firstName || ''} ${balance.userId?.lastName || ''}`.trim() ||
                        balance.userId?.phoneNumber ||
                        'Unknown';

                      const employeeId = balance.userId?.employeeId || balance.userId?.phoneNumber || 'N/A';

                      const assignedByName = balance.assignedBy?.name ||
                        balance.assignedBy?.fullName ||
                        `${balance.assignedBy?.firstName || ''} ${balance.assignedBy?.lastName || ''}`.trim() ||
                        balance.assignedBy?.phoneNumber ||
                        'Unknown';

                      return (
                        <tr key={balance._id}>
                          <td>
                            <div>
                              <div style={{ fontWeight: '500' }}>{employeeName}</div>
                              <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>{employeeId}</div>
                            </div>
                          </td>
                          <td>{balance.leaveTypeId?.name || 'N/A'}</td>
                          <td>{balance.year}</td>
                          <td>{balance.totalLeaves}</td>
                          <td>{balance.usedLeaves}</td>
                          <td>{balance.remainingLeaves}</td>
                          <td>{assignedByName}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Weekly Offs Tab */}
        {activeTab === 'weekly-offs' && (
          <div className="tab-content">
            <div className="section-header">
              <h2>Weekly Off Configuration</h2>
              <p>Select which days of the week are weekly offs</p>
            </div>

            <div className="weekly-off-config">
              <div className="day-selection">
                {dayNames.map((dayName, index) => (
                  <label key={index} className="day-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedWeeklyOffs.includes(index)}
                      onChange={() => handleWeeklyOffChange(index)}
                    />
                    <span className="day-name">{dayName}</span>
                  </label>
                ))}
              </div>

              <button
                className="btn btn-primary"
                onClick={handleSaveWeeklyOffs}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>

            {/* Current Configuration */}
            <div className="current-config">
              <h3>Current Weekly Offs</h3>
              {weeklyOffs.length === 0 ? (
                <p>No weekly offs configured</p>
              ) : (
                <div className="weekly-off-list">
                  {weeklyOffs.map((wo) => (
                    <div key={wo._id} className="weekly-off-item">
                      <span className="day-name">{wo.dayName}</span>
                      <span className="description">{wo.description}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default LeavePolicyManagementPage;